import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../Components/Layout/Layout";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Cookie helpers
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};
const getCookie = (name) => {
  const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
  return m ? decodeURIComponent(m.pop()) : null;
};

// Helper to format employee names
const empLabel = (u) => `${u.first_name} ${u.last_name}`.trim();

export default function OrganizationList() {
  const navigate = useNavigate();

  // Data state
  const [orgs, setOrgs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locOptions, setLocOptions] = useState([]);
  const [schedules, setSchedules] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);

  // Form fields
  const [orgName, setOrgName] = useState("");
  const [locException, setLocException] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [respSel, setRespSel] = useState([]);
  const [locSel, setLocSel] = useState([]);

  // Show/Hide inactive users — default from cookie
  const [showInactive, setShowInactive] = useState(false);
  useEffect(() => {
    const v = getCookie("show_inactive_users");
    setShowInactive(v === "1");
  }, []);

  // ===== employee options (как в TeamList) =====
  const employeeBaseOptions = useMemo(() => {
    if (showInactive) return employees;
    return employees.filter((u) => {
      const flag = u.is_active ?? u.active;
      return flag === undefined ? true : Boolean(flag);
    });
  }, [employees, showInactive]);

  // Ensure selected responsibles remain visible even if inactive
  const employeeOptionsWithSelected = useMemo(() => {
    const map = new Map(employeeBaseOptions.map((u) => [String(u.id), u]));
    respSel.forEach((u) => {
      const k = String(u.id);
      if (!map.has(k)) map.set(k, u);
    });
    return Array.from(map.values());
  }, [employeeBaseOptions, respSel]);

  // Fetch data
  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token") || "";
      const res = await fetch(
        `${API_URL}/admin/organizations?auth_token=${encodeURIComponent(token)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setOrgs(data.organizations || []);
      } else {
        console.error("Failed to fetch organizations:", data.message);
      }
    } catch (err) {
      console.error("Network error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token") || "";
      const res = await fetch(
        `${API_URL}/admin/users?auth_token=${encodeURIComponent(token)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setEmployees(data.users || []);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/allowed-locations`);
      const data = await res.json();
      const pts = (data.points || []).map((p) => ({
        id: `P${p.id}`,
        label: `📍 ${p.name || `Zone #${p.id}`}`,
      }));
      const polys = (data.polygons || []).map((a) => ({
        id: `A${a.id}`,
        label: `🔷 ${a.name || `Area #${a.id}`}`,
      }));
      setLocOptions([...pts, ...polys]);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token") || "";
      const res = await fetch(
        `${API_URL}/admin/schedules?auth_token=${encodeURIComponent(token)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setSchedules(data.schedules || []);
      }
    } catch (err) {
      console.error("Error fetching schedules:", err);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
    fetchEmployees();
    fetchLocations();
    fetchSchedules();
  }, [fetchOrganizations, fetchEmployees, fetchLocations, fetchSchedules]);

  // ===== Utils for display =====

  // как в TeamList: понимает id ИЛИ chat_id
  const parseEmployees = (csv) => {
    if (!csv) return [];
    const tokens = csv.split(",").map((s) => s.trim()).filter(Boolean);
    return tokens
      .map((tok) => {
        let emp = employees.find((e) => String(e.id) === tok);
        if (!emp) emp = employees.find((e) => String(e.chat_id) === tok);
        return emp ? empLabel(emp) : tok;
      })
      .filter(Boolean);
  };

  const locNamesFromIds = (idsStr) => {
    if (!idsStr) return [];
    const ids = idsStr.split(",").map((s) => s.trim());
    return ids
      .map((id) => {
        const o = locOptions.find((l) => l.id === id);
        return o ? o.label.replace(/^.\s/, "") : id;
      })
      .filter(Boolean);
  };

  const concise = (arr) =>
    arr.length > 3 ? arr.slice(0, 3).join(", ") + " ..." : arr.join(", ");

  // Form handling
  const resetForm = () => {
    setOrgName("");
    setLocException(false);
    setSelectedSchedules([]);
    setRespSel([]);
    setLocSel([]);
  };

  const openCreateDlg = () => {
    resetForm();
    setOpenCreate(true);
  };

  const openEditDlg = (org) => {
    setEditingOrg(org);
    setOrgName(org.name || "");
    setLocException(!!org.location_exception);

    // schedules
    const orgSchedules = org.schedules || [];
    setSelectedSchedules(
      orgSchedules
        .map((s) =>
          schedules.find((sched) => sched.schedule_id === s.schedule_id)
        )
        .filter(Boolean)
    );

    // responsibles: умный toSel по id/chat_id (как в TeamList)
    const csvResp =
      org.responsibles || org.responsible_ids || org.responsible_users || "";
    const toSel = (csv, opts) => {
      if (!csv) return [];
      const tokens = csv.split(",").map((i) => i.trim()).filter(Boolean);
      return tokens
        .map((tok) => {
          let u = opts.find((o) => String(o.id) === tok);
          if (!u) u = opts.find((o) => String(o.chat_id) === tok);
          return u || null;
        })
        .filter(Boolean);
    };
    setRespSel(toSel(csvResp, employees));

    // locations
    const lSel = locNamesFromIds(org.allowed_locations)
      .map((nm) => locOptions.find((o) => o.label.includes(nm)))
      .filter(Boolean);
    setLocSel(lSel);

    setOpenEdit(true);
  };

  // Save handlers
  const buildPayload = () => ({
    name: orgName,
    auth_token: localStorage.getItem("auth_token") || "",
    location_exception: locException,
    start_times: selectedSchedules.map((s) => s.schedule_id).join(","), // schedule IDs
    responsibles: respSel.map((u) => u.id).join(","),                  // сохраняем только ID
    allowed_locations: locSel.map((o) => o.id).join(","),
  });

  const createOrg = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/organizations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchOrganizations();
        setOpenCreate(false);
      } else {
        console.error("Create organization error:", data.message);
      }
    } catch (err) {
      console.error("Network error:", err);
    }
  };

  const saveEdit = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/organizations/${editingOrg.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchOrganizations();
        setOpenEdit(false);
      } else {
        console.error("Edit organization error:", data.message);
      }
    } catch (err) {
      console.error("Network error:", err);
    }
  };

  const deleteOrg = async (id) => {
    if (!window.confirm("Delete organization?")) return;

    const token = localStorage.getItem("auth_token") || "";
    const url = `${API_URL}/admin/organizations/${id}?auth_token=${encodeURIComponent(
      token
    )}`;

    try {
      const res = await fetch(url, { method: "DELETE" });
      const data = await res.json();
      if (data.status === "success") {
        fetchOrganizations();
      } else {
        console.error("Delete organization error:", data.message);
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      console.error("Delete organization network error:", err);
      alert("Network error while deleting organization");
    }
  };

  const toggleShowInactive = (e) => {
    const v = e.target.checked;
    setShowInactive(v);
    setCookie("show_inactive_users", v ? "1" : "0");
  };

  // Table actions
  const viewStats = (org) =>
    navigate(`/admin/entity_timesheet_detail/organization/${org.id}`);
  const userRole = localStorage.getItem("userRole") || "";

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">Organizations</Typography>

        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox checked={showInactive} onChange={toggleShowInactive} />
            }
            label="Show deactivated users (affects selectors)"
          />
        </Box>

        {["ceo", "admin"].includes(userRole.toLowerCase()) && (
          <Button sx={{ mt: 1 }} variant="contained" onClick={openCreateDlg}>
            Create organization
          </Button>
        )}

        {loading ? (
          <Typography sx={{ mt: 3 }}>Loading…</Typography>
        ) : (
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Loc exception</TableCell>
                <TableCell>Schedules</TableCell>
                <TableCell>Responsibles</TableCell>
                <TableCell>Allowed locations</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((org) => {
                // берём любые возможные поля с csv
                const respCsv =
                  org.responsibles ||
                  org.responsible_ids ||
                  org.responsible_users ||
                  "";
                const resp = parseEmployees(respCsv);
                const locs = locNamesFromIds(org.allowed_locations);

                return (
                  <TableRow key={org.id}>
                    <TableCell>{org.id}</TableCell>
                    <TableCell>{org.name}</TableCell>
                    <TableCell>
                      {org.location_exception ? "Да" : "Нет"}
                    </TableCell>
                    <TableCell>
                      {(org.schedules || []).map((s) => (
                        <Chip
                          key={s.schedule_id}
                          label={`${s.start_times} (${s.days_of_week})`}
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      {resp.length ? concise(resp) : "-"}
                    </TableCell>
                    <TableCell>
                      {locs.length ? concise(locs) : "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => viewStats(org)}
                        sx={{ mr: 1 }}
                      >
                        <VisibilityIcon fontSize="small" /> View
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditDlg(org)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => deleteOrg(org.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* Create dialog */}
        <Dialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Create organization</DialogTitle>
          <DialogContent>
            <TextField
              label="Name"
              fullWidth
              sx={{ mt: 2 }}
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={locException}
                  onChange={(e) => setLocException(e.target.checked)}
                />
              }
              label="Исключение локации"
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={schedules}
              getOptionLabel={(o) =>
                `${o.start_times.replace(",", "-")} (${o.days_of_week})`
              }
              value={selectedSchedules}
              onChange={(e, v) => setSelectedSchedules(v)}
              renderInput={(params) => (
                <TextField {...params} label="Schedules" />
              )}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={employeeOptionsWithSelected}
              getOptionLabel={empLabel}
              value={respSel}
              onChange={(e, v) => setRespSel(v)}
              isOptionEqualToValue={(opt, val) =>
                String(opt.id) === String(val.id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Responsibles" />
              )}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={locOptions}
              getOptionLabel={(o) => o.label}
              value={locSel}
              onChange={(e, v) => setLocSel(v)}
              renderInput={(params) => (
                <TextField {...params} label="Allowed locations" />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button variant="contained" onClick={createOrg}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit dialog */}
        <Dialog
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Edit organization</DialogTitle>
          <DialogContent>
            <TextField
              label="Name"
              fullWidth
              sx={{ mt: 2 }}
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={locException}
                  onChange={(e) => setLocException(e.target.checked)}
                />
              }
              label="Исключение локации"
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={schedules}
              getOptionLabel={(o) =>
                `${o.start_times.replace(",", "-")} (${o.days_of_week})`
              }
              value={selectedSchedules}
              onChange={(e, v) => setSelectedSchedules(v)}
              renderInput={(params) => (
                <TextField {...params} label="Schedules" />
              )}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={employeeOptionsWithSelected}
              getOptionLabel={empLabel}
              value={respSel}
              onChange={(e, v) => setRespSel(v)}
              isOptionEqualToValue={(opt, val) =>
                String(opt.id) === String(val.id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Responsibles" />
              )}
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={locOptions}
              getOptionLabel={(o) => o.label}
              value={locSel}
              onChange={(e, v) => setLocSel(v)}
              renderInput={(params) => (
                <TextField {...params} label="Allowed locations" />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveEdit}>
              Save changes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
